// InputPanel - Formulaire de saisie de la situation clinique (version sans données patient)

import { useState } from 'react';

export interface SoinInput {
  description: string;
}

export type AnalysisMode = 'local' | 'expert';

interface InputPanelProps {
  loading: boolean;
  onSubmit: (info: SoinInput) => void;
  onReset: () => void;
  hasResults: boolean;
  analysisMode: AnalysisMode;
  onModeChange: (mode: AnalysisMode) => void;
  expertAvailable: boolean;
}

const PRESET_SCENARIOS = [
  {
    label: 'Diabète + ulcère (cumul)',
    description: 'Injection d\'insuline matin et soir, pansement complexe sur ulcère veineux jambe gauche, INR capillaire le matin.',
  },
  {
    label: 'Post-opératoire (Art. 7)',
    description: 'Patient en post-opératoire RAAC, surveillance clinique postopératoire matin, retrait de sonde urinaire, pansement courant sur cicatrice abdominale.',
  },
  {
    label: 'BSI + perf + injection',
    description: 'Patient sous BSI validé, dépendance lourde BSC, toilette complète matin, perfusion courte antibiotique 45min sous surveillance, injection héparine matin.',
  },
  {
    label: 'Multi-actes (cumul 100/50/0)',
    description: 'Pansement courant sur plaie chirurgicale, injection héparine sous-cutanée, prise de sang veineuse — même visite, patient diabétique.',
  },
  {
    label: 'Dialyse péritonéale (Art. 6)',
    description: 'Séance de dialyse péritonéale à domicile matin, surveillance tension artérielle, pansement simple sur cathéter.',
  },
];

export default function InputPanel({ loading, onSubmit, onReset, hasResults, analysisMode, onModeChange, expertAvailable }: InputPanelProps) {
  const [description, setDescription] = useState('');

  const applyPreset = (scenario: typeof PRESET_SCENARIOS[number]) => {
    setDescription(scenario.description);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) return;
    onSubmit({ description: description.trim() });
  };

  const handleReset = () => {
    setDescription('');
    onReset();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Mode selector */}
      <div>
        <label className="text-xs font-semibold text-foreground-500 uppercase tracking-wider block mb-2">
          Mode d&apos;analyse
        </label>
        <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
          <button
            type="button"
            onClick={() => onModeChange('local')}
            disabled={loading}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
              analysisMode === 'local'
                ? 'bg-background-50 text-foreground-950 shadow-sm'
                : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            <i className="ri-cpu-line text-sm"></i>
            Analyse locale
          </button>
          <button
            type="button"
            onClick={() => onModeChange('expert')}
            disabled={loading || !expertAvailable}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
              !expertAvailable
                ? 'text-foreground-300 cursor-not-allowed'
                : analysisMode === 'expert'
                  ? 'bg-background-50 text-foreground-950 shadow-sm'
                  : 'text-foreground-500 hover:text-foreground-700'
            }`}
            title={!expertAvailable ? 'Clé OpenAI non configurée' : 'Analyse experte par GPT-4o'}
          >
            <i className="ri-sparkling-line text-sm"></i>
            Analyse experte
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">GPT-4o</span>
          </button>
        </div>
        {!expertAvailable && (
          <p className="text-[11px] text-foreground-400 mt-1.5 ml-1">
            <i className="ri-information-line text-xs mr-0.5"></i>
            L&apos;analyse experte nécessite une clé API OpenAI configurée dans les secrets Supabase.
          </p>
        )}
        {analysisMode === 'expert' && expertAvailable && (
          <p className="text-[11px] text-accent-600 mt-1.5 ml-1">
            <i className="ri-sparkling-line text-xs mr-0.5"></i>
            GPT-4o analysera votre description en profondeur avec les tarifs NGAP 2026.
          </p>
        )}
      </div>

      {/* Preset scenarios */}
      {!hasResults && (
        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-foreground-400 pt-1">Exemples :</span>
          {PRESET_SCENARIOS.map((s) => (
            <button
              key={s.label}
              type="button"
              onClick={() => applyPreset(s)}
              className="px-3 py-1.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap border border-secondary-200/50"
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      {/* Description textarea */}
      <div>
        <label className="text-xs font-semibold text-foreground-500 uppercase tracking-wider block mb-2">
          Situation clinique
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Décrivez les soins réalisés : ex. Injection insuline matin et soir, pansement ulcère jambe gauche, INR capillaire matin."
          rows={5}
          maxLength={500}
          disabled={loading}
          className="w-full bg-background-50 border border-background-300/60 rounded-lg px-4 py-3 text-sm text-foreground-800 placeholder:text-foreground-300 resize-none focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30 transition-all disabled:opacity-50"
        />
        <p className="text-xs text-foreground-400 mt-1.5 text-right">{description.length}/500</p>
      </div>

      {/* Submit */}
      <div className="flex items-center gap-3 pt-2">
        <button
          type="submit"
          disabled={loading || !description.trim()}
          className={`flex items-center gap-2 px-6 py-2.5 rounded-md text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
            loading || !description.trim()
              ? 'bg-background-200 text-foreground-400 cursor-not-allowed'
              : analysisMode === 'expert'
                ? 'bg-accent-500 text-white hover:bg-accent-600'
                : 'bg-primary-500 text-white hover:bg-primary-600'
          }`}
        >
          {loading ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              {analysisMode === 'expert' ? 'GPT-4o analyse...' : 'Analyse en cours...'}
            </>
          ) : (
            <>
              <i className={analysisMode === 'expert' ? 'ri-sparkling-line' : 'ri-brain-line'}></i>
              {analysisMode === 'expert' ? 'Analyser avec GPT-4o' : 'Analyser la cotation'}
            </>
          )}
        </button>

        {hasResults && !loading && (
          <button
            type="button"
            onClick={handleReset}
            className="px-5 py-2.5 rounded-md text-sm font-medium text-foreground-600 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap border border-background-300/60"
          >
            <i className="ri-refresh-line mr-1"></i>
            Nouvelle analyse
          </button>
        )}
      </div>
    </form>
  );
}